/**
 * The status a consumer renders. Derived from the persisted session status plus
 * the client-side recording phase (the vendor keeps producing notes for
 * several minutes after `stop`, which the persisted `inProgress` status cannot
 * express).
 */
export enum ScribeStatus {
  Idle = 'idle',
  RequestingPermission = 'requestingPermission',
  PermissionDenied = 'permissionDenied',
  Connecting = 'connecting',
  InProgress = 'inProgress',
  Writing = 'writing',
  Ready = 'ready',
  Failed = 'failed',
}

/**
 * Why microphone access could not be obtained, so a consumer can vary the
 * recovery copy: the browser blocked the prompt vs no capture device exists.
 */
export enum MicrophoneDenialReason {
  Denied = 'denied',
  Unavailable = 'unavailable',
}

/**
 * The rendered statuses that mean a session is live end-to-end, but NOT the
 * terminal `ready`/`failed` states a finished session lingers in for review.
 * Shared so consumers gate on the same set.
 */
export const SCRIBE_IN_PROGRESS_STATUSES: readonly ScribeStatus[] = [
  ScribeStatus.RequestingPermission,
  ScribeStatus.Connecting,
  ScribeStatus.InProgress,
  ScribeStatus.Writing,
];

/**
 * The terminal error statuses a consumer treats as dismissable: a failed
 * session and a blocked microphone. Shared so consumers gate on the same set.
 */
export const SCRIBE_ERROR_STATUSES: readonly ScribeStatus[] = [
  ScribeStatus.Failed,
  ScribeStatus.PermissionDenied,
];

/**
 * Client-side capture phase. Distinguishes the pre-recording microphone
 * handshake (which precedes any session doc), the connecting window (session
 * doc created, lease + token mint + SDK init in flight but no audio captured
 * yet) and the post-stop writing window from live recording, while the
 * persisted session status is still `inProgress`. There is deliberately no
 * pause phase — the SDK is start/stop only.
 */
export enum ScribeRecordingPhase {
  Idle = 'idle',
  RequestingPermission = 'requestingPermission',
  PermissionDenied = 'permissionDenied',
  Connecting = 'connecting',
  Recording = 'recording',
  Writing = 'writing',
}

/**
 * The capture phases that mean a live session is already under way, so a
 * second start must be refused. Shared so consumers gate on the same set.
 */
export const SCRIBE_ACTIVE_PHASES: readonly ScribeRecordingPhase[] = [
  ScribeRecordingPhase.RequestingPermission,
  ScribeRecordingPhase.Connecting,
  ScribeRecordingPhase.Recording,
  ScribeRecordingPhase.Writing,
];
